import {
  Css,
  GridTableLayout,
  Icon,
  Palette,
  RowStyles,
  column,
  emptyCell,
  selectColumn,
  simpleHeader,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useMemo } from "react";
import { GlobalPlanMilestoneFilter } from "src/generated/graphqlTypes";
import {
  MilestoneCatalogPage_GlobalPlanMilestoneFragment,
  MilestoneCatalogPage_GlobalPlanMilestoneGroupFragment,
} from "src/routes/libraries/milestone-catalog/MilestoneCatalogPage.generated";
import { createMilestoneCatalogFormUrl } from "src/RouteUrls";
import { isDefined } from "src/utils";

type MilestoneCatalogTableProps = {
  globalPlanMilestones: MilestoneCatalogPage_GlobalPlanMilestoneFragment[];
  layoutState?: ReturnType<typeof useGridTableLayoutState<GlobalPlanMilestoneFilter>>;
  maybeFetchMore: () => void;
};

export function MilestoneCatalogTable(props: MilestoneCatalogTableProps) {
  const { globalPlanMilestones, layoutState, maybeFetchMore } = props;
  const rows = useMemo(() => createRows(globalPlanMilestones ?? []), [globalPlanMilestones]);
  const columns = useMemo(() => createColumns(), []);

  return (
    <GridTableLayout
      hideEditColumns
      layoutState={layoutState}
      tableProps={{ columns, rows, as: "virtual", infiniteScroll: { onEndReached: maybeFetchMore }, rowStyles }}
    />
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: MilestoneCatalogPage_GlobalPlanMilestoneFragment };

type Row = HeaderRow | DataRow;

const rowStyles: RowStyles<Row> = { data: { rowLink: (row) => createMilestoneCatalogFormUrl(row.data.id) } };

function createColumns() {
  return [
    selectColumn<Row>(),
    column<Row>({ header: "Milestone Name", data: ({ name }) => <span>{name}</span>, mw: "240px" }),
    column<Row>({ header: "Constraints", data: ({ constraints }) => <span>{constraints.length}</span>, mw: "120px" }),
    column<Row>({ header: "Team", data: ({ groups }) => <span>{formatTeamColumn(groups)}</span>, mw: "180px" }),
    column<Row>({
      header: "Default",
      data: ({ isDefault }) => (isDefault ? <Icon icon={"checkCircle"} color={Palette.Gray600} /> : emptyCell),
      mw: "100px",
    }),
    column<Row>({
      header: () => (
        <div css={Css.df.jcsb.gap1.aic.$}>
          <div>System</div>
          <Icon
            color={Palette.Gray600}
            inc={2}
            icon="infoCircle"
            tooltip="These milestones power reporting pages and other metrics, and therefore cannot be deleted."
          />
        </div>
      ),
      mw: "110px",
      data: ({ systemPlanMilestone }) =>
        isDefined(systemPlanMilestone) ? <Icon icon={"checkCircle"} color={Palette.Gray600} /> : emptyCell,
    }),
    column<Row>({
      header: () => (
        <div css={Css.df.jcsb.gap1.aic.$}>
          <div>Invoice Milestone</div>
          <Icon
            color={Palette.Gray600}
            inc={2}
            icon="infoCircle"
            tooltip="These milestones can be selected as invoice triggers when configuring invoice terms, and completing them generates the corresponding invoices."
          />
        </div>
      ),
      mw: "180px",
      data: ({ isInvoiceMilestone }) =>
        isInvoiceMilestone ? <Icon icon={"checkCircle"} color={Palette.Gray600} /> : emptyCell,
    }),
  ];
}

function createRows(data: MilestoneCatalogPage_GlobalPlanMilestoneFragment[]) {
  return [
    simpleHeader,
    ...data.map((globalPlanMilestone) => ({
      id: globalPlanMilestone.id,
      kind: "data" as const,
      data: globalPlanMilestone,
    })),
  ];
}

// helper function to grab all the milestone group's business functions
function formatTeamColumn(groups: MilestoneCatalogPage_GlobalPlanMilestoneGroupFragment[]) {
  const groupByBusinessFunction = groups.groupBy((group) => group.businessFunctionType.name);
  return Object.keys(groupByBusinessFunction)
    .map((businessFunction) => businessFunction)
    .join(", ");
}
